import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IProfileListClassification } from "../../../../../../models/profile-list-classification";
import MainApi from "../../../../../../services/mainApi.service";
import {
  createProfileListClassifications,
  updateProfileListClassifications,
} from "../../../../../../services/profileListClassifications.service";
import {
  profileListClassificationSchema,
  TProfileListClassificationForm,
} from "./profileListClassificationSchema";

const translationPath = "page.register.roles.";

const LIST_CLASSIFICATION_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/ListClassification/";
const listClassificationService = new MainApi<ISelectType>(LIST_CLASSIFICATION_ROUTE);

interface IProfileListClassificationProps {
  id?: string;
  roleId?: string;
  rows: IProfileListClassification[];
  onBack: () => void;
}

export const ProfileListClassification = memo(
  ({ id, roleId, rows, onBack }: IProfileListClassificationProps) => {
    const { t } = useTranslation();

    const { sendRequest: create, loading: cLoading } = useFetch(
      createProfileListClassifications
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateProfileListClassifications
    );

    const row = useMemo(
      () => rows.find((currentRow) => currentRow.id === id) ?? null,
      [rows, id]
    );

    const getHandler = useCallback(async (): Promise<
      Treturn<TProfileListClassificationForm>
    > => {
      const formData: TProfileListClassificationForm = {
        listClassification: row
          ? { id: row.listClassification.id, name: row.listClassification.name }
          : null,
      };

      return { data: formData, success: true };
    }, [row]);

    const updateClassificationIds = useCallback(
      (classificationId: string) =>
        Array.from(
          new Set(
            rows.map((currentRow) =>
              currentRow.id === row?.id ? classificationId : currentRow.listClassificationId
            )
          )
        ),
      [row?.id, rows]
    );

    const saveHandler = (v: TProfileListClassificationForm) =>
      create({
        roleId: roleId ?? "",
        listClassificationIds: v.listClassification ? [v.listClassification.id] : [],
      });

    const updateHandler = (v: TProfileListClassificationForm) =>
      update({
        id: roleId ?? "",
        item: {
          roleId: roleId ?? "",
          listClassificationIds: v.listClassification
            ? updateClassificationIds(v.listClassification.id)
            : rows.map((currentRow) => currentRow.listClassificationId),
        },
      });

    const loading = cLoading || uLoading;

    return (
      <FormContainer<TProfileListClassificationForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "list_classification")}
        schema={profileListClassificationSchema}
        isModalForm={true}
        subtitle={
          id
            ? t(translationPath + "edit_link")
            : t(translationPath + "add_list_classification")
        }
        backButtonOnClick={onBack}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AutoComplete
                request={listClassificationService.list}
                name="listClassification"
                size="small"
                getOptionLabel={(option: ISelectType) => option?.name ?? ""}
                label={t(translationPath + "list_classification")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  }
);
